import { useState, useEffect, useSyncExternalStore } from 'react';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';

export const PageFlags = {
  DEMO_DASHBOARD: 'demo_dashboard',
  DEMO_VAULT: 'demo_vault',
  DEMO_COMPOSER: 'demo_composer',
  DEMO_DEAL_PIPELINE: 'demo_deal_pipeline',
  DEMO_PEOPLE: 'demo_people',
  DEMO_INBOX: 'demo_inbox',
  DEMO_CALENDAR: 'demo_calendar',
  DEMO_DELIVERY_MANAGER: 'demo_delivery_manager',
  DEMO_INTEGRATIONS: 'demo_integrations',
  DEMO_PORTFOLIO: 'demo_portfolio',
  DEMO_CONVERSATIONS: 'demo_conversations',
  DEMO_CONTRACT_IQ: 'demo_contract_iq',

  PROD_DASHBOARD: 'prod_dashboard',
  PROD_VAULT: 'prod_vault',
  PROD_COMPOSER: 'prod_composer',
  PROD_DEAL_PIPELINE: 'prod_deal_pipeline',
  PROD_PEOPLE: 'prod_people',
  PROD_INBOX: 'prod_inbox',
  PROD_CALENDAR: 'prod_calendar',
  PROD_DELIVERY_MANAGER: 'prod_delivery_manager',
  PROD_INTEGRATIONS: 'prod_integrations',
  PROD_PORTFOLIO: 'prod_portfolio',
  PROD_CONVERSATIONS: 'prod_conversations',
  PROD_CONTRACT_IQ: 'prod_contract_iq',
} as const;

export const ElementFlags = {
  HIDE_CALENDAR: 'hide_calendar',
} as const;

export const FeatureFlags = { ...PageFlags, ...ElementFlags } as const;

export type PageFlagName = (typeof PageFlags)[keyof typeof PageFlags];
export type ElementFlagName = (typeof ElementFlags)[keyof typeof ElementFlags];
export type FeatureFlagName = PageFlagName | ElementFlagName;
export type FeatureFlagWhen = 'enabled' | 'disabled';

// ---------------------------------------------------------------------------
// Flag defaults — used when PostHog is unavailable or a flag is undefined.
// Demo flags default enabled (demo should work out of the box).
// Production flags default disabled (gated until feature is ready).
// ---------------------------------------------------------------------------
const FLAG_DEFAULTS: Record<FeatureFlagName, boolean> = {
  [PageFlags.DEMO_DASHBOARD]: true,
  [PageFlags.DEMO_VAULT]: true,
  [PageFlags.DEMO_COMPOSER]: true,
  [PageFlags.DEMO_DEAL_PIPELINE]: true,
  [PageFlags.DEMO_PEOPLE]: true,
  [PageFlags.DEMO_INBOX]: true,
  [PageFlags.DEMO_CALENDAR]: true,
  [PageFlags.DEMO_DELIVERY_MANAGER]: true,
  [PageFlags.DEMO_INTEGRATIONS]: true,
  [PageFlags.DEMO_PORTFOLIO]: true,
  [PageFlags.DEMO_CONVERSATIONS]: true,
  [PageFlags.DEMO_CONTRACT_IQ]: true,

  [PageFlags.PROD_DASHBOARD]: false,
  [PageFlags.PROD_VAULT]: true,
  [PageFlags.PROD_COMPOSER]: false,
  [PageFlags.PROD_DEAL_PIPELINE]: true,
  [PageFlags.PROD_PEOPLE]: true,
  [PageFlags.PROD_INBOX]: false,
  [PageFlags.PROD_CALENDAR]: false,
  [PageFlags.PROD_DELIVERY_MANAGER]: false,
  [PageFlags.PROD_INTEGRATIONS]: false,
  [PageFlags.PROD_PORTFOLIO]: true,
  [PageFlags.PROD_CONVERSATIONS]: false,
  [PageFlags.PROD_CONTRACT_IQ]: true,

  [ElementFlags.HIDE_CALENDAR]: false,
};

// ---------------------------------------------------------------------------
// Environment detection
// ---------------------------------------------------------------------------
const _posthogConfigured =
  import.meta.env.VITE_POSTHOG_ENABLED === 'true' && !!import.meta.env.VITE_POSTHOG_KEY;

const _isProduction = import.meta.env.VITE_APP_ENVIRONMENT === 'production';

// ---------------------------------------------------------------------------
// PostHog readiness state — shared across all hook instances
// ---------------------------------------------------------------------------
const POSTHOG_TIMEOUT_MS = 3000;

let _flagsLoaded = false;
let _flagsTimedOut = false;
const _listeners = new Set<() => void>();

function getFlagsReady() {
  return _flagsLoaded || _flagsTimedOut;
}

function subscribe(cb: () => void) {
  _listeners.add(cb);
  return () => { _listeners.delete(cb); };
}

function markReady(via: 'loaded' | 'timeout') {
  if (via === 'loaded') _flagsLoaded = true;
  else _flagsTimedOut = true;
  _listeners.forEach(cb => cb());
}

// ---------------------------------------------------------------------------
// Flag resolution
// ---------------------------------------------------------------------------
function getFlagDefault(flag: FeatureFlagName): boolean {
  if (!_isProduction) return true;
  return FLAG_DEFAULTS[flag] ?? false;
}

function resolveFlag(
  flag: FeatureFlagName,
  posthogValue: boolean | undefined,
  ready: boolean,
): boolean | null {
  if (!_posthogConfigured) return getFlagDefault(flag);

  if (ready) return posthogValue ?? getFlagDefault(flag);

  return null;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
export function useFeatureFlag(flag: FeatureFlagName): boolean | null {
  const enabled = useFeatureFlagEnabled(flag);
  const posthog = usePostHog();
  const ready = useSyncExternalStore(subscribe, getFlagsReady);

  useEffect(() => {
    if (!posthog || !_posthogConfigured || _flagsLoaded) return;

    posthog.onFeatureFlags(() => markReady('loaded'));

    const timer = setTimeout(() => {
      if (!_flagsLoaded) markReady('timeout');
    }, POSTHOG_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [posthog]);

  if (!_isProduction) return true;

  return resolveFlag(flag, enabled, ready);
}

export function matchFeatureFlagValue(
  flagValue: boolean | null,
  when: FeatureFlagWhen = 'enabled',
): boolean | null {
  if (flagValue === null) return null;
  return when === 'enabled' ? flagValue : !flagValue;
}

export function useFeatureFlagMatch(
  flag: FeatureFlagName,
  when: FeatureFlagWhen = 'enabled',
): boolean | null {
  const value = useFeatureFlag(flag);
  return matchFeatureFlagValue(value, when);
}
